import { useCallback, useRef, useEffect } from "react";
import clickSoundFile from "@/assets/sounds/click.mp3";
import profileClickSoundFile from "@/assets/sounds/profile-click.mp3";

type SoundType = "nav" | "profile";

type UseClickSoundOptions = {
  volume?: number; // 0..1
  sound?: SoundType;
};

const SOUNDS: Record<SoundType, string> = {
  nav: clickSoundFile,
  profile: profileClickSoundFile,
};

// Shared AudioContext for all instances (mobile browsers limit contexts)
let sharedContext: AudioContext | null = null;
let isUnlocked = false;

// Pre-decoded audio buffers for instant playback
const audioBuffers: Map<string, AudioBuffer> = new Map();

function getAudioContext(): AudioContext {
  if (!sharedContext) {
    sharedContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return sharedContext;
}

// Unlock audio on first user interaction (required for mobile)
function unlockAudio() {
  if (isUnlocked) return;
  
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    ctx.resume().then(() => {
      isUnlocked = true;
    }).catch(() => {});
  } else {
    isUnlocked = true;
  }
}

// Pre-load and decode audio file
async function loadSound(url: string): Promise<AudioBuffer | null> {
  if (audioBuffers.has(url)) {
    return audioBuffers.get(url)!;
  }
  
  try {
    const ctx = getAudioContext();
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
    audioBuffers.set(url, audioBuffer);
    return audioBuffer;
  } catch (e) {
    console.warn('[useClickSound] Failed to load sound:', e);
    return null;
  }
}

/**
 * Ultra-low-latency UI click sound using Web Audio API.
 * - Pre-decodes audio for instant playback
 * - Handles mobile audio unlock automatically
 * - Uses shared AudioContext to avoid mobile limits
 */
export function useClickSound(options: UseClickSoundOptions = {}) {
  const { volume = 0.5, sound = "nav" } = options;
  const bufferRef = useRef<AudioBuffer | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  // Pre-load the sound on mount
  useEffect(() => {
    const soundUrl = SOUNDS[sound];
    loadSound(soundUrl).then(buffer => {
      bufferRef.current = buffer;
    });
    
    // Set up gain node for volume control
    const ctx = getAudioContext();
    gainNodeRef.current = ctx.createGain();
    gainNodeRef.current.gain.value = volume;
    gainNodeRef.current.connect(ctx.destination);
    
    // Add unlock listeners for mobile
    const handleInteraction = () => unlockAudio();
    document.addEventListener('touchstart', handleInteraction, { once: true, passive: true });
    document.addEventListener('click', handleInteraction, { once: true });
    
    return () => {
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('click', handleInteraction);
    };
  }, [sound, volume]);

  const play = useCallback(() => {
    // Ensure audio is unlocked
    unlockAudio();
    
    const buffer = bufferRef.current;
    const gainNode = gainNodeRef.current;
    
    if (!buffer || !gainNode) return;
    
    try {
      const ctx = getAudioContext();
      
      // Resume context if suspended (mobile requirement)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // Create a new buffer source for each play (they're one-shot)
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(gainNode);
      source.start(0);
    } catch (e) {
      // Silent fail - don't break UI for sound issues
    }
  }, []);

  return { play };
}
